import { useCallback, useEffect, useState } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Bold, Italic, Link, Strikethrough } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/lib/utils';

type ToolbarState = Readonly<{
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  link: boolean;
}>;

function getSelectedNode(selection: ReturnType<typeof $getSelection>) {
  if (!$isRangeSelection(selection)) return null;
  const anchor = selection.anchor.getNode();
  const focus = selection.focus.getNode();
  if (anchor === focus) return anchor;
  return selection.isBackward() ? focus : anchor;
}

function readToolbarState(): ToolbarState {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return {
      bold: false,
      italic: false,
      strikethrough: false,
      link: false,
    };
  }

  const selectedNode = getSelectedNode(selection);
  const parent = selectedNode?.getParent() ?? null;
  const isLink =
    (selectedNode !== null && $isLinkNode(selectedNode)) ||
    (parent !== null && $isLinkNode(parent));

  return {
    bold: selection.hasFormat('bold'),
    italic: selection.hasFormat('italic'),
    strikethrough: selection.hasFormat('strikethrough'),
    link: isLink,
  };
}

function shallowEqualToolbar(a: ToolbarState, b: ToolbarState): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.strikethrough === b.strikethrough &&
    a.link === b.link
  );
}

export type ToolbarPluginProps = {
  disabled?: boolean;
  className?: string;
};

export function ToolbarPlugin({ disabled = false, className }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();

  const [state, setState] = useState<ToolbarState>(() => ({
    bold: false,
    italic: false,
    strikethrough: false,
    link: false,
  }));

  const updateFromEditor = useCallback(() => {
    editor.getEditorState().read(() => {
      const next = readToolbarState();
      setState((prev) => (shallowEqualToolbar(prev, next) ? prev : next));
    });
  }, [editor]);

  useEffect(() => {
    // Initial read.
    updateFromEditor();

    const unregisterUpdate = editor.registerUpdateListener(() => {
      updateFromEditor();
    });

    const unregisterSelectionChange = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateFromEditor();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterUpdate();
      unregisterSelectionChange();
    };
  }, [editor, updateFromEditor]);

  const toggleBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const toggleStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const toggleLink = useCallback(() => {
    if (state.link) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const url = window.prompt('Link URL');
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === '') return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed);
  }, [editor, state.link]);

  const toolbarButtonClass = 'h-7 w-7 px-0';

  return (
    <div className={cn('lexical-toolbar', className)} aria-disabled={disabled}>
      <Button
        type="button"
        size="sm"
        variant={state.bold ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleBold}
        disabled={disabled}
        aria-label="Bold"
      >
        <Bold className="size-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={state.italic ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleItalic}
        disabled={disabled}
        aria-label="Italic"
      >
        <Italic className="size-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={state.strikethrough ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleStrikethrough}
        disabled={disabled}
        aria-label="Strikethrough"
      >
        <Strikethrough className="size-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={state.link ? 'secondary' : 'ghost'}
        className={toolbarButtonClass}
        onClick={toggleLink}
        disabled={disabled}
        aria-label="Link"
      >
        <Link className="size-4" />
      </Button>
    </div>
  );
}
